/**
 * MText entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Direction-3 — typed sibling of `../mtextToRenderable.ts`. Reads the
 * canonical `MtextEntity` fields directly off the model (`model.ts`:
 * `mtextEntitySchema` — `insertionPoint` / `nominalHeight` / `rotation` /
 * `lineSpacingFactor` / `attachmentPoint` / `styleName` / `contents`) instead
 * of the legacy `EntityPreview.properties` string bag. Produces a
 * byte-identical `RenderableEntity` (golden-parity) for the same entity.
 *
 * # Golden-parity quirks reproduced EXACTLY (NOT "better")
 *
 *   - Insertion z: `mtextEntitySchema.insertionPoint` is the 2D
 *     `drawingPointSchema` (no z), AND the producer `buildMTextPreview`
 *     (dxfGeometry.ts) writes ONLY `"Insertion X"` + `"Insertion Y"` into the
 *     bag — it emits NO `"Insertion Z"`. The legacy adapter then reads
 *     `readNumber(props, "Insertion Z")` → 0. This adapter feeds `z: 0` to the
 *     shared builder for identical output.
 *
 *   - Width factor: `mtextEntitySchema` carries NO width-factor field (DXF
 *     group 41 is the reference-rectangle WIDTH on MTEXT, modelled as
 *     `referenceWidth`, NOT a glyph width factor). `buildMTextPreview` writes
 *     NO `"Width factor"` key, so the legacy adapter's
 *     `readPositiveNumber(props, "Width factor", 1)` always returns the
 *     default 1. This adapter passes the SAME constant 1 — matching the legacy
 *     path exactly (the renderer's MTEXT runs scale only via the per-run `\W`
 *     code, which `parseMTextContent` already handles).
 *
 *   - Height: `nominalHeight` is `z.number().positive()` so it is always
 *     positive; the legacy `readPositiveNumber(props, "Height", 2.5)` returns
 *     it verbatim (the 2.5 fallback never triggers for a valid model entity).
 *     Passed through unchanged.
 *
 *   - Attachment: clamped to 1..9 with the SAME guard the legacy adapter uses
 *     (`Number.isFinite && >=1 && <=9 ? round : 1`).
 *
 *   - Style name: `styleName` defaults to "Standard" in the schema; the SAME
 *     `.trim() || "Standard"` normalisation the legacy adapter applies is used
 *     so a blank/whitespace name falls back identically.
 *
 *   - Contents: `entity.contents` (the legacy adapter reads
 *     `props.Contents ?? entity.label`; the bag's `Contents` is exactly
 *     `entity.contents`, so the typed read matches — the `label` fallback only
 *     fires when `Contents` is absent, which never happens for a real MText).
 *
 *   - Colour backfill: identical to the legacy path — runs that carry no
 *     explicit `\C` override are parsed with a placeholder colour, then the
 *     resolved `RenderableEntity.colorHex` is backfilled over the placeholder
 *     on the shared `geometry.runs` AFTER `buildRenderableShell`.
 *
 * Category B: standalone file; NOT imported into any render path yet.
 */

import {
  effectiveMTextAngleDeg,
  isReflectedOcs,
} from "@dxf-ai-editor/command-engine";
import type { MtextEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity } from "../../types";

import {
  optionalPointZ,
  positiveOr,
  type AdapterContext,
} from "../adapterContext";
import { assembleMTextRenderable } from "../geom/mtextGeometry";
import { resolveTypedAnnotation } from "./annotativeResolve";

/**
 * Typed adapter for the `MText` entity (`mtextEntitySchema`).
 *
 * S10d — a matched annotative representation overrides the per-scale insertion
 * / rotation and scales the height by `effectiveScale`. VISIBILITY (hiding an
 * annotative MTEXT with no representation at the active scale) is gated in
 * `renderableFromDrawingEntity`, so this adapter keeps its non-null return.
 */
export function mtextFromEntity(
  entity: MtextEntity,
  ctx: AdapterContext,
): RenderableEntity {
  // S10d — resolve the active-scale annotative representation (single truth).
  // No-op for non-annotative MTEXT / no active scale (effectiveScale 1, no
  // insertion/rotation override).
  const anno = resolveTypedAnnotation(entity.annotativeContexts, ctx);

  const attachmentRaw = entity.attachmentPoint ?? 1;
  const attachmentPoint =
    Number.isFinite(attachmentRaw) && attachmentRaw >= 1 && attachmentRaw <= 9
      ? Math.round(attachmentRaw)
      : 1;
  const styleName = (entity.styleName ?? "Standard").trim() || "Standard";
  const contents = entity.contents ?? "";
  // Codex #342 round-8 Finding B — extrusion handedness from the FULL OCS normal
  // (shared `isReflectedOcs`), not a brittle local `z < 0` (which mishandled
  // `−0`/floating noise). Absent ⇒ the (0,0,1) default ⇒ not mirrored (golden
  // parity). Mirroring a default MTEXT → (0,0,−1) ⇒ mirrored.
  const mirrored = isReflectedOcs(entity.extrusion);
  // Codex #342 round-8 Finding A — the EFFECTIVE render angle. A custom group-11
  // direction vector WINS over the `rotation` attribute (ezdxf get_rotation), so
  // a group-11-only MTEXT (rotation 0, custom dir) renders at the vector angle —
  // not the round-7 raw-rotation scalar (which is stored for export self-inverse).
  // Non-custom (default {1,0,0}) ⇒ returns `rotation` verbatim (golden parity).
  const renderRotation = effectiveMTextAngleDeg(
    entity.xAxisDirection,
    entity.rotation ?? 0,
  );
  // S10d — the per-scale context rotation supersedes the direction-derived
  // render angle when a representation matched (mirrors the legacy override);
  // `anno.rotation` is undefined for the non-annotative / no-match case, so the
  // direction-derived `renderRotation` is used unchanged.
  const rotation = anno.rotation ?? renderRotation;

  // Assembly + shell + placeholder→resolved-colour backfill are the shared
  // one-truth in ../geom/mtextGeometry.ts (`assembleMTextRenderable`); this
  // adapter only reads the canonical model fields it needs.
  return assembleMTextRenderable(entity, ctx, {
    // `insertionPoint` is 2D on the schema → a top-level entity reads 0
    // (the legacy bag carries NO "Insertion Z" — golden parity intact). A
    // block-flatten clone carries a REAL transformed z (@codex #431 P2 — Z
    // preserve): read it via the shared `optionalPointZ`, like Text. The
    // per-scale annotative insertion (S10d) overrides x/y when matched.
    insertionRaw: {
      x: anno.insertion?.x ?? entity.insertionPoint.x,
      y: anno.insertion?.y ?? entity.insertionPoint.y,
      z: optionalPointZ(entity.insertionPoint),
    },
    // `nominalHeight` is schema-`.positive()`, so it equals the legacy
    // `readPositiveNumber("Height", 2.5)` verbatim. An annotative MTEXT renders
    // at paper_height × effectiveScale model units (effectiveScale is 1 for the
    // non-annotative / no-match case).
    height: entity.nominalHeight * anno.effectiveScale,
    rotation,
    // The MTEXT model has no glyph width-factor field; the legacy bag has no
    // "Width factor" key → default 1. Pass the SAME constant.
    widthFactor: 1,
    // `lineSpacingFactor` is `z.number().default(1)` — a 0/negative model value
    // is admissible, and the legacy `readPositiveNumber("Line spacing factor",
    // 1)` clamps it to 1. Apply the SAME positive-or-default clamp (Codex P2).
    lineSpacingFactor: positiveOr(entity.lineSpacingFactor, 1),
    attachmentPoint,
    styleName,
    contents,
    mirrored,
  });
}
